import React, { useEffect } from "react";
import Paper from "@mui/material/Paper";
import FormFader from "../Common/Fader/FormFader";
import { Box, Grid, Hidden } from "@mui/material";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import Group from "../../Wizard/Group";
import PersonIcon from "../../../assets/icons/provision/toggleable/IconPerson";
import LegalEntityIcon from "../../../assets/icons/contract/toggleable/IconCompany";
import Address from "../Common/Address/Address";
import Person from "../Common/Person/Person";
import { emailDefault, isPhoneNumberDefault, requiredValidator } from "../../../services/validationRules";
import { useForm } from "../../../provider/Form/FormProvider";
import ValidatorTextField from "../../FormFields/ValidatorTextField";
import Typography from "@mui/material/Typography";
import FormLegend from "../Common/FormLegend/FormLegend";
import PostOfficeBoxAddress from "../Common/Address/PostOfficeBoxAddress";
import FormSubmitProps from "../../../types/FormSubmitProps";
import wizardStyle from "../wizardStyle";
import CheckIcon from "../../../assets/icons/common/toggleable/IconCheck";
import CrossIcon from "../../../assets/icons/common/toggleable/IconClose";
import ValidatorSelectField from "../../FormFields/ValidatorSelectField";
import MenuItem from "@mui/material/MenuItem";
import { Child } from "../../../types/Entities/Child";

type OpposingPartyProps = {
  submitProps: FormSubmitProps;
};

export default function OpposingParty({ submitProps }: OpposingPartyProps) {
  const { values, handleSubmit, handleChange } = useForm();

  useEffect(() => {
    if (!values.witnessesToBeRemoved) {
      values.witnessesToBeRemoved = [];
    }

    handleChange({
      target: {
        name: "witnessCount",
        value: values.witnesses.length.toString(),
        type: "text",
      },
    });
  }, []);

  useEffect(() => {
    const witnessCount = parseInt(values.witnessCount, 10);

    if (values.witnesses.length < witnessCount) {
      for (let i = values.witnesses.length; i < witnessCount; i++) {
        handleChange({
          target: {
            name: `witnesses.${i}`,
            value: { gender: "", givenName: "", familyName: "", id: `new-${i}` },
            type: "text",
          },
        });
      }
    }

    if (values.witnesses.length > witnessCount) {
      let updated = [];

      for (let i = 0; i < values.witnesses.length; i++) {
        if (i < witnessCount) {
          updated.push(values.witnesses[i]);
        } else {
          if (!(typeof values.witnesses[i].id === "string" && values.witnesses[i].id.startsWith("new-"))) {
            values.witnessesToBeRemoved.push(values.witnesses[i].id);
          }
        }
      }

      handleChange({
        target: {
          name: `witnesses`,
          value: updated,
          type: "text",
        },
      });
    }
  }, [values.witnessCount]);

  useEffect(() => {
    if (values.hasWitness === "no") {
      handleChange({
        target: {
          name: "witnessCount",
          value: null,
          type: "text",
        },
      });

      for (let i = 0; i < values.witnesses.length; i++) {
        if (!(typeof values.witnesses[i].id === "string" && values.witnesses[i].id.startsWith("new-"))) {
          values.witnessesToBeRemoved.push(values.witnesses[i].id);
        }
      }

      handleChange({
        target: {
          name: "witnesses",
          value: [],
          type: "text",
        },
      });
    } else {
      handleChange({
        target: {
          name: "witnessCount",
          value: values.witnesses.length > 0 ? values.witnesses.length.toString() : "1",
          type: "text",
        },
      });
    }
  }, [values.hasWitness]);

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Hidden smUp>
            <Typography variant={"h4"} sx={wizardStyle.headline}>
              Ihr Anliegen
            </Typography>
          </Hidden>
          <Grid container justifyContent={"flex-start"} alignItems={"baseline"}>
            <Grid item xs={12}>
              <FormLegend>
                <strong>Angaben zur Gegenseite</strong>
              </FormLegend>
            </Grid>
            <Grid item xs={12} sx={[wizardStyle.gridTopSpaced, wizardStyle.question]}>
              <FormLegend>
                Damit wir rechtliche Schritte einleiten können, benötigen wir möglichst ausführliche Angaben zu der gegnerischen Partei, gegen die Sie rechtlich
                vorgehen möchten.
              </FormLegend>
            </Grid>
            <Grid item xs={12}>
              <Group
                type="radio"
                name="opposingPartyType"
                question="Handelt es sich um ein Unternehmen oder eine Privatperson?"
                options={[
                  {
                    labelText: "Unternehmen",
                    labelIcon: <LegalEntityIcon />,
                    labelIconActive: <LegalEntityIcon active />,
                    value: "opposingPartyLegalEntity",
                  },
                  {
                    labelText: "Privatperson",
                    labelIcon: <PersonIcon />,
                    labelIconActive: <PersonIcon active />,
                    value: "opposingPartyPerson",
                  },
                ]}
                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
              />
            </Grid>
            {values.opposingPartyType === "opposingPartyLegalEntity" && (
              <>
                <Grid item xs={12} sx={[wizardStyle.gridTopSpaced, wizardStyle.question]}>
                  <FormLegend>Wie heißt das Unternehmen, gegen das Sie rechtlich vorgehen möchten?</FormLegend>
                </Grid>
                <Grid item xs={12}>
                  <ValidatorTextField
                    name="opposingPartyLegalEntity.legalName"
                    label="Name des Unternehmens"
                    helperText="Bitte geben Sie den vollständigen Namen der Firma an."
                    validators={[requiredValidator("Damit wir wissen, gegen wen wir rechtlich vorgehen müssen, benötigen wir den Namen der Gegenseite.")]}
                  />
                </Grid>
                <Grid item xs={12} sx={[wizardStyle.gridTopSpaced, wizardStyle.question]}>
                  <FormLegend>Bitte geben Sie möglichst alle weiteren Daten an, die Ihnen vorliegen, damit wir für Sie tätig werden können.</FormLegend>
                </Grid>
                <Address path="opposingPartyLegalEntity.address" headline={""} fieldsAreRequired={false} />
                <PostOfficeBoxAddress path="opposingPartyLegalEntity.address" headline={""} fieldsAreRequired={false} />
                <Grid item xs={12}>
                  <ValidatorTextField
                    name="opposingPartyLegalEntity.email"
                    label="E-Mail-Adresse Gegenseite"
                    autoComplete="email"
                    validators={[emailDefault]}
                  />
                </Grid>
                <Grid item xs={12}>
                  <ValidatorTextField
                    name="opposingPartyLegalEntity.telephone"
                    label="Telefonnummer Gegenseite"
                    autoComplete="tel"
                    validators={isPhoneNumberDefault}
                  />
                </Grid>
              </>
            )}
            {values.opposingPartyType === "opposingPartyPerson" && (
              <>
                <Person path="opposingPartyPerson" headline="Wie heißt die Person, gegen die Sie rechtlich vorgehen möchten?" />
                <Grid item xs={12} sx={[wizardStyle.gridTopSpaced, wizardStyle.question]}>
                  <FormLegend>Bitte geben Sie möglichst alle weiteren Daten an, die Ihnen vorliegen, damit wir für Sie tätig werden können.</FormLegend>
                </Grid>
                <Address path="opposingPartyPerson.residenceAddress" headline="" fieldsAreRequired={false} />
                <Grid item xs={12}>
                  <ValidatorTextField name="opposingPartyPerson.email" label="E-Mail-Adresse Gegenseite" autoComplete="email" validators={[emailDefault]} />
                </Grid>
                <Grid item xs={12}>
                  <ValidatorTextField
                    name="opposingPartyPerson.telephone"
                    label="Telefonnummer Gegenseite"
                    autoComplete="tel"
                    validators={isPhoneNumberDefault}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <Group
                type="radio"
                name="hasWitness"
                question="Gibt es Zeugen für den Vorfall?"
                options={[
                  {
                    labelText: "Ja, es gibt Zeugen",
                    labelIcon: <CheckIcon />,
                    labelIconActive: <CheckIcon active />,
                    value: "yes",
                  },
                  {
                    labelText: "Nein, es gibt keine Zeugen",
                    labelIcon: <CrossIcon />,
                    labelIconActive: <CrossIcon active />,
                    value: "no",
                  },
                ]}
                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
              />
            </Grid>
            {values.hasWitness === "yes" && (
              <>
                <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
                  <FormLegend>Wie viele Zeugen gibt es?</FormLegend>
                </Grid>
                <Grid item xs={12}>
                  <ValidatorSelectField
                    label="Anzahl Zeugen"
                    name="witnessCount"
                    validators={[requiredValidator("Bitte geben Sie an, wie viele Zeugen es gibt.")]}
                  >
                    <MenuItem value="1">1</MenuItem>
                    <MenuItem value="2">2</MenuItem>
                    <MenuItem value="3">3</MenuItem>
                    <MenuItem value="4">4</MenuItem>
                    <MenuItem value="5">5</MenuItem>
                  </ValidatorSelectField>
                </Grid>
              </>
            )}
            {values.hasWitness === "yes" &&
              parseInt(values.witnessCount, 10) > 0 &&
              values.witnesses.map((child: Child, index: number) => (
                <Person
                  key={child.id}
                  path={`witnesses.${index}`}
                  headline={`Bitte machen Sie hier weitere Angaben zu der ${index + 1}. Person, die Sie als Zeugen angeben möchten.`}
                />
              ))}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
